import { Favorite, Search } from "@mui/icons-material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {
	Autocomplete,
	CircularProgress,
	Dialog,
	Grid,
	InputAdornment,
	InputLabel,
	Skeleton,
	TextField,
	Typography,
} from "@mui/material";
import { fetchCustomerLocations } from "actions/customerActions";
import { fetchHardwareApprovers } from "actions/hardwareApproversActions";
import { createOrder, createOrderRequest } from "actions/orderActions";
import { fetchManager, fetchUsers, sendMail } from "actions/userActions";
import clsx from "clsx";
import { PrimaryFormButton, SecondaryButton } from "components/Common/Buttons/Button";
import { CountrySelector } from "components/Common/CountrySelector";
import { HardwareDrawer } from "components/Hardware/SelectHardware/HardwareDrawer";
import { Input } from "components/Input";
import { MobilePhoneInput } from "components/MobilePhoneInput";
import { SummaryCart } from "components/MyPeople/EmployeeDialogSteps/Summary/SummaryCart";
import { ShippingDetails } from "components/MyPeople/EmployeeDialogSteps/UserDetails/ShippingDetails";
import { getCode, getData } from "country-list";
import {
	selectCustomerDetails,
	selectCustomerLocations,
	selectHardwareDeliveryCountries,
} from "features/customer";
import { selectHardwareApprovers } from "features/hardware/hardwareApprovers";
import { addItemsfromPurchaseCart } from "features/hardware/hardwareBundles";
import { selectHardwarePurchaseType, setHardwareDrawerOpen } from "features/hardware/hardwarePage";
import {
	productsSelectors,
	selectCartCount,
	selectCartCountBySku,
	selectCartPriceTotal,
} from "features/hardware/products";
import { selectManager, selectUsers, usersSelectors } from "features/users";
import { useApiOnce, useAppSelector, useAuth } from "hooks/hooks";
import _ from "lodash";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { RootState } from "store";
import { CustomerLocation, HardwareApprover, User } from "types";
import { IRONSTONE_SUPPORT_MAIL } from "utilities/constants/constants";
import {
	DeliveryOption,
	HardwareDeliveryCountryOption,
	HardwareDrawerType,
	HardwarePurchaseType,
} from "utilities/constants/enums";
import { NAVIGATION_NAMES } from "utilities/constants/pages";
import { getApprovers } from "utilities/hardwareApprovers";
import { calculateHardwarePrice } from "utilities/hardwareUtils/hardwareCostUtils";
import useRole from "utilities/roleUtils/roleCheck";
import {
	mapObjectStringValues,
	trimAndRemoveDuplicateSpacesBetweenWords,
} from "utilities/stringFormattingUtility";
import HardwareCheckoutComplete from "../HardwareCheckoutComplete/HardwareCheckoutComplete";
import { DeliveryLocationSelector } from "./DeliveryLocationSelector";
import { DeliveryOptions } from "./DeliveryOptions";
import styles from "./HardwareCheckout.module.scss";

interface Props {
	onFinishCheckout: () => void;
}

const HardwareCheckout = ({ onFinishCheckout }: Props) => {
	const customerInfo = useAppSelector(selectCustomerDetails);
	const customerLocations = useAppSelector(selectCustomerLocations);
	const [selectedUser, setSelectedUser] = useState<User>();
	const [selectedDeliveryLocation, setSelectedDeliveryLocation] =
		useState<CustomerLocation | null>(null);
	const hardwarePurchaseType = useAppSelector(selectHardwarePurchaseType);
	const hardwareDeliveryCountries = useAppSelector(selectHardwareDeliveryCountries);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [orderFailed, setOrderFailed] = useState(false);
	const [showCartDialog, setShowCartDialog] = useState(false);
	const userState = useAppSelector(selectUsers);
	const allUsers = useAppSelector(usersSelectors.selectEntities);

	const {
		dispatch,
		auth,
		auth: {
			account: { localAccountId },
		},
	} = useAuth();

	const { isActionRole, isAdmin } = useRole();

	const userGraphData = useAppSelector((state) =>
		usersSelectors.selectById(state, localAccountId),
	);

	const { isLoading: isManagerOfThisUserLoading } = useAppSelector((state: RootState) =>
		selectManager(state, localAccountId),
	);

	const productsById = useAppSelector(productsSelectors.selectEntities);
	const cartItems = useAppSelector(selectCartCountBySku);
	const cartItemsCount = useAppSelector(selectCartCount);
	const cartPriceTotal = useAppSelector(selectCartPriceTotal);

	const users = useAppSelector(usersSelectors.selectAll);

	useApiOnce(fetchUsers, useAppSelector(selectUsers));
	useApiOnce(fetchCustomerLocations, customerLocations);

	const approversState = useAppSelector(selectHardwareApprovers);
	useApiOnce(fetchHardwareApprovers, approversState);

	useEffect(() => {
		if (!userGraphData?.id) {
			return;
		}

		dispatch(fetchManager({ auth, id: userGraphData?.id }));
	}, [dispatch, auth, userGraphData?.id]);

	const defaultProps = {
		options: users,
		getOptionLabel: (option: User) => option.displayName ?? "",
		isOptionEqualToValue: (option: User, value: User) => option.id === value.id,
	};

	const formProps = useForm<FieldValues>({
		mode: "onSubmit",
	});

	const {
		control,
		register,
		handleSubmit,
		formState: { errors, touchedFields },
		setValue,
		reset,
		getValues,
		watch,
	} = formProps;

	const handleSelectUser = (user: User) => {
		// We need to reset the touchedFields, so that the useEffect below will
		// consider these values as not touched yet, and overwrite current values with selected user values
		setSelectedUser(user);
		const valuesToSet = {
			id: user.id,
			mail: user.mail ?? user.userPrincipalName,
			mobilePhone: user.mobilePhone ?? user.businessPhones?.find(Boolean) ?? "+47",
			givenName: user.givenName,
			surname: user.surname,
			streetAddress: "",
			postalCode: "",
			city: "",
		};
		Object.entries(valuesToSet).forEach(([key, value]) => {
			(touchedFields as Record<string, boolean>)[key] = true; // FIXME: This is a hack to get the form to work
			setValue(key, value);
		});
	};

	const currentCountryValue = watch("country");

	// Initialize country field when component mounts or when purchase type changes
	useEffect(() => {
		// Only hardcode country when purchase type is 'Buy' (not leasing)
		if (hardwarePurchaseType === HardwarePurchaseType.Buy) {
			if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Norway) {
				setValue("country", "Norway");
			} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Sweden) {
				setValue("country", "Sweden");
			} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Any) {
				// When delivery countries is "Any", default to Norway
				setValue("country", "Norway");
			}
		}
	}, [hardwarePurchaseType, hardwareDeliveryCountries, setValue]);

	// Handle location selection changes
	useEffect(() => {
		if (selectedDeliveryLocation?.country !== currentCountryValue) {
			setSelectedDeliveryLocation(null);
			setValue("shippingCountry", null);
			setValue("streetAddress", null);
			setValue("postalCode", null);
			setValue("city", null);
		}
		if (currentCountryValue && hardwarePurchaseType === HardwarePurchaseType.Lease) {
			setValue("country", currentCountryValue);
			setValue("shippingCountry", currentCountryValue);
		} else if (hardwarePurchaseType === HardwarePurchaseType.Buy) {
			if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Norway) {
				setValue("country", "Norway");
			} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Sweden) {
				setValue("country", "Sweden");
			} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Any) {
				setValue("country", "Norway");
			}
		} else {
			setValue("country", null);
			setValue("shippingCountry", null);
			setValue("streetAddress", null);
			setValue("postalCode", null);
			setValue("city", null);
		}
	}, [selectedDeliveryLocation, currentCountryValue]);

	const deliveryOption = watch("deliveryOption");

	useEffect(() => {
		let userData;
		const allValues = getValues();
		if (allValues.deliveryOption === DeliveryOption.B2B) {
			userData = {
				...userGraphData,
				allValues,
			};
		} else if (allValues.deliveryOption === DeliveryOption.B2C) {
			userData = selectedUser ?? {};
			setSelectedDeliveryLocation(null);
		}

		const { id, givenName, surname, mail, userPrincipalName, mobilePhone, businessPhones } =
			userData ?? {};

		// Get the values of the fields that have been touched
		const touchedFieldValues = Object.keys(touchedFields).reduce(
			(acc, key) => {
				if (touchedFields[key]) {
					acc[key] = allValues[key];
				}
				return acc;
			},
			{} as { [key: string]: any },
		);

		// Certain values should be persisted when swapping between delivery options
		const valuesToPersist = {
			deliveryOption: allValues.deliveryOption,
			country: allValues.country,
			companyName: allValues.companyName,
			note: allValues.note,
			...touchedFieldValues,
		};

		// And certain values should be set when swapping between delivery options
		const valuesToSet = {
			id,
			givenName,
			surname,
			mail: mail ?? userPrincipalName,
			mobilePhone: mobilePhone ?? businessPhones?.find(Boolean) ?? "+47",
			streetAddress: "",
			postalCode: "",
			city: "",
		};

		// Persisted values set last, so that they overwrite the values to set
		const finalValues = {
			...valuesToSet,
			...valuesToPersist,
		};

		Object.entries(finalValues).forEach(([key, value]) => {
			setValue(key, value);
		});
	}, [
		selectedUser,
		userGraphData,
		setValue,
		customerInfo.departmentName,
		getValues,
		deliveryOption,
	]);

	const inputs = [
		{
			name: "givenName",
			label: "First name",
		},
		{
			name: "surname",
			label: "Last name",
		},
		{
			name: "mail",
			label: (
				<>
					<Typography variant="body1" display="inline" mr={1}>
						Email
					</Typography>
					<Typography variant="caption" color="text.secondary" display="inline">
						(Receiver of shipping information)
					</Typography>
				</>
			),
		},
	];

	const handleCheckout = async () => {
		const employeeData = getValues();

		mapObjectStringValues(employeeData, trimAndRemoveDuplicateSpacesBetweenWords);

		const {
			id,
			givenName,
			surname,
			mail,
			mobilePhone,
			city,
			streetAddress,
			postalCode,
			messageToApprover,
			deliveryOption,
			country,
			note,
			companyName,
		} = employeeData;

		setIsSubmitting(true);

		const currency = productsById[Object.keys(cartItems)[0]]?.priceInfo.currency;

		const orderLines = Object.entries(cartItems).map(([sku, quantity]) => {
			const { price } = calculateHardwarePrice(
				productsById[sku]?.priceInfo,
				hardwarePurchaseType,
			);
			const { displayName } = productsById[sku] ?? {};
			return {
				sku,
				quantity,
				displayName,
				price: String(price),
				link: `https://www.komplettbedrift.no/product/${sku}`,
			};
		});

		const order = {
			messageToSalesRep: "",
			purchaserFirmName:
				deliveryOption === DeliveryOption.B2B ? companyName : `${givenName} ${surname}`,
			deliveryOption,
			isLeasingOrder: hardwarePurchaseType === HardwarePurchaseType.Lease,
			purchaserFirmAddress: streetAddress,
			purchaserFirmPostalCode: postalCode,
			purchaserFirmCity: city,
			purchaserFirmCountryCode: getCode(country ?? "N/A") ?? "NO",
			contactPersonName: `${givenName} ${surname}`,
			contactPersonEmail: mail,
			contactPersonPhone: mobilePhone,
			orderLines: orderLines,
			// Extra fields for the mail sent by the Logic App to the requester / approver
			totalPrice: String(cartPriceTotal),
			currency: currency,
			messageToApprover,
			hardwareNote: note,
		} as { [key: string]: any };

		const submitOrder = isActionRole ? createOrder : createOrderRequest;
		const idForOrder = isActionRole ? id : localAccountId;

		const res = await dispatch(submitOrder({ auth, id: idForOrder, body: order }));

		// If success, and user is action role (i.e its an actual order, not an order request)
		const shouldSendMail = res.meta.requestStatus === "fulfilled" && isActionRole;

		if (shouldSendMail) {
			dispatch(
				sendMail({
					auth,
					body: {
						mailType: "HARDWARE_ORDER",
						recipientMail: userGraphData?.mail ?? userGraphData?.userPrincipalName,
						recipientName: `${userGraphData?.givenName} ${userGraphData?.surname}`,
						messageBody: {
							onboardedUserMail: mail,
							hardwareBought: orderLines.map(
								({ displayName: name, quantity, price: cost, link }) => ({
									name,
									quantity,
									cost,
									link,
								}),
							),
							totalCost: cartPriceTotal,
							shippingAddress: `${streetAddress}, ${postalCode} ${city}, ${country}`,
							linkToIronstonePage: `${window.location.origin}${NAVIGATION_NAMES.ORDERS.path}`,
						},
					},
				}),
			);
		} else if (res.meta.requestStatus === "rejected") {
			setOrderFailed(true);
		}

		setIsSubmitting(false);
		setIsSubmitted(true);
	};

	const [approvers, setApprovers] = useState<HardwareApprover[]>([]);
	const [message, setMessage] = useState("");
	const [loadingApproverFinalized, setLoadingApproverFinalized] = useState(false);

	useEffect(() => {
		if (
			isManagerOfThisUserLoading === false &&
			approversState.isLoading === false &&
			userState.isLoading === false
		) {
			const manager = allUsers[userGraphData?.manager ?? ""] ?? ({} as User);
			const { approvers, message } = getApprovers(manager, approversState.approvers);
			setApprovers(approvers);
			setMessage(message);
			setLoadingApproverFinalized(true);
		}
	}, [isManagerOfThisUserLoading, approversState.isLoading, userState.isLoading]);

	const disableSubmitButton =
		!_.isEmpty(errors) || cartItemsCount === 0 || (!isActionRole && approvers.length === 0);

	const isHomeDelivery = watch("deliveryOption") === DeliveryOption.B2C;

	const handleSelectDeliveryLocation = (location: CustomerLocation | null) => {
		setSelectedDeliveryLocation(location);
		if (!location) {
			// Reset the form if the user deselects the location
			reset({
				streetAddress: "",
				postalCode: "",
				city: "",
			});
			// Reset country field based on purchase type
			if (hardwarePurchaseType === HardwarePurchaseType.Buy) {
				if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Norway) {
					setValue("country", "Norway");
				} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Sweden) {
					setValue("country", "Sweden");
				} else if (hardwareDeliveryCountries === HardwareDeliveryCountryOption.Any) {
					setValue("country", "Norway");
				}
			} else {
				setValue("country", currentCountryValue ?? null);
				setValue("shippingCountry", currentCountryValue ?? null);
			}
			return;
		}
		// Reset any errors that might have been set
		delete errors.streetAddress;
		delete errors.postalCode;
		delete errors.city;
		// Set the values of the selected location
		setValue("streetAddress", location.address);
		setValue("postalCode", location.postalCode);
		setValue("city", location.city);

		// Set the country based on countryCode (same logic as before)
		if (location.countryCode === "NO") {
			setValue("country", "Norway");
			setValue("shippingCountry", "Norway"); // Also set shippingCountry for the UI
		} else if (location.countryCode === "SE") {
			setValue("country", "Sweden");
			setValue("shippingCountry", "Sweden"); // Also set shippingCountry for the UI
		} else {
			// For other countries, convert country code to country name
			const countryName =
				getData().find((country: any) => country.code === location.countryCode)?.name ||
				location.countryCode;
			setValue("country", countryName);
			setValue("shippingCountry", countryName); // Also set shippingCountry for the UI
		}
	};

	if (orderFailed) {
		return (
			<Grid container className={styles.orderFailedContainer}>
				<Typography variant="h2">Unfortunately, something went wrong</Typography>
				<Typography variant="body1">
					We were unable to process your order. <br />
					Please contact support at:{" "}
					{<a href="mailto:name@email.com">{IRONSTONE_SUPPORT_MAIL}</a>} with the order
					details.
				</Typography>
			</Grid>
		);
	}

	return (
		<form onSubmit={handleSubmit(handleCheckout)} id="personal-data-form">
			<Grid container mb={6}>
				{isSubmitting && (
					<Dialog open classes={{ container: styles.submitDialog }}>
						<Grid container p={3}>
							<CircularProgress size={30} color="primary" />
							<Typography variant="h5" ml={2}>
								{`${isActionRole ? "Placing" : "Requesting"} hardware ${
									isActionRole ? "order" : ""
								}, please wait...`}
							</Typography>
						</Grid>
					</Dialog>
				)}
				{isSubmitted ? (
					<HardwareCheckoutComplete
						onFinishCheckout={onFinishCheckout}
						approvers={approvers}
						{...getValues()}
					/>
				) : (
					<>
						{showCartDialog && <HardwareDrawer />}
						<Grid item className={styles.formContainer}>
							<Grid container item className={styles.header}>
								<LocalShippingIcon />
								<Typography variant="h2">Shipping details</Typography>
							</Grid>

							<DeliveryOptions control={control} />
							<Grid item xs={12}>
								<Grid
									className={clsx({
										[styles.fixedHeight]: isActionRole,
										[styles.hidden]: !isActionRole,
									})}
								>
									{isHomeDelivery ? (
										<Autocomplete
											{...defaultProps}
											onChange={(_, newValue) => {
												handleSelectUser(
													newValue ?? ({ displayName: "" } as User),
												);
											}}
											fullWidth
											autoSelect
											autoHighlight
											size="medium"
											renderOption={(props, option) => (
												<li {...props} key={option.id}>
													<Typography variant="body1">
														{option.displayName}
													</Typography>
												</li>
											)}
											disabled={!isActionRole}
											renderInput={(params) => (
												<TextField
													{...params}
													variant="outlined"
													placeholder="Search for employee"
													InputProps={{
														...params.InputProps,
														startAdornment: (
															<>
																<InputAdornment position="start">
																	<Search />
																</InputAdornment>
																{params.InputProps.startAdornment}
															</>
														),
													}}
												/>
											)}
										/>
									) : (
										<Input
											name="companyName"
											label="Company name"
											register={register}
											errors={errors}
											disabled={false}
											defaultValue={customerInfo?.departmentName ?? ""}
										/>
									)}
								</Grid>
							</Grid>
							{hardwarePurchaseType === HardwarePurchaseType.Lease && (
								<Grid item xs={6} mb={3}>
									<InputLabel>
										<Typography variant="body1" display="inline" mr={1}>
											Delivery country
										</Typography>
									</InputLabel>
									<CountrySelector
										value={watch("country")}
										register={register}
										setValue={setValue}
										isLeasing={true}
									/>
								</Grid>
							)}
							{!isHomeDelivery && (
								<DeliveryLocationSelector
									selectedDeliveryCountry={watch("country")}
									selectedDeliveryLocation={selectedDeliveryLocation}
									handleSelectDeliveryLocation={handleSelectDeliveryLocation}
									hardwarePurchaseType={hardwarePurchaseType}
								/>
							)}
							<ShippingDetails
								formProps={formProps}
								xs={12}
								disabled={selectedDeliveryLocation !== null && !isHomeDelivery}
								countryDisabled={watch("shippingCountry")}
							/>

							<Grid container item xs={12} mb={3}>
								<Grid item xs={12}>
									<Grid container>
										{inputs.map(({ name, label }) => (
											<Grid
												container
												key={name}
												className={styles.inputContainer}
											>
												<Input
													name={name}
													disabled={false}
													label={label}
													register={register}
													errors={errors}
												/>
											</Grid>
										))}
									</Grid>
									<Grid item xs={6}>
										<MobilePhoneInput
											formProps={formProps}
											fullWidth
											required={true}
											handleOnBlur={() => {}}
											isOnboarding={false}
										/>
									</Grid>
								</Grid>

								<Grid container justifyContent="flex-start" item xs={12}>
									{isActionRole && (
										<Grid item xs={8} mt={3}>
											<InputLabel>
												<Typography variant="body1" display="inline" mr={1}>
													Additional information
												</Typography>
												<Typography
													variant="body1"
													color="text.secondary"
													display="inline"
												>
													(optional)
												</Typography>
											</InputLabel>
											<TextField
												multiline
												placeholder="Notes will be shown inside the order history table"
												rows={3}
												fullWidth
												size="small"
												InputProps={{
													classes: {
														input: styles.formTextInput,
													},
												}}
												{...register("note")}
											/>
										</Grid>
									)}
									{!isActionRole && (
										<Grid container mt={3}>
											<Grid container item xs={8}>
												{loadingApproverFinalized ? (
													<Typography
														variant="description"
														fontWeight={500}
														className={clsx({
															[styles.noApproverStyle]:
																approvers.length === 0,
														})}
													>
														{message}
													</Typography>
												) : (
													<Grid
														container
														className={styles.approverSkeleton}
													>
														<Skeleton
															variant="text"
															width="70%"
															height={30}
														/>
													</Grid>
												)}
											</Grid>
											<Grid item xs={8} mt={0.5}>
												<TextField
													multiline
													rows={2.4}
													fullWidth
													size="small"
													InputProps={{
														classes: {
															root: styles.streetAddress,
															input: styles.formTextInput,
														},
													}}
													error={!!errors.streetAddress}
													{...register("messageToApprover")}
													disabled={approvers.length === 0}
												/>
											</Grid>
										</Grid>
									)}
								</Grid>
							</Grid>
							<PrimaryFormButton
								text={isActionRole ? "Place order" : "Request hardware"}
								size="large"
								variantStyle="contained"
								isDisabled={disableSubmitButton}
								form="personal-data-form"
								action={() => {}}
							/>
						</Grid>

						<Grid item className={styles.orderSummaryContainer}>
							<SummaryCart />
							<Grid
								item
								xs={12}
								display="flex"
								justifyContent="flex-end"
								className={styles.createBundleFromCartButton}
							>
								{isAdmin && (
									<SecondaryButton
										text={"Create bundle from cart"}
										size="medium"
										variantStyle="outlined"
										isDisabled={!_.isEmpty(errors) || cartItemsCount === 0}
										icon={
											<Favorite
												color="primary"
												className={styles.heartIcon}
											/>
										}
										action={() => {
											dispatch(addItemsfromPurchaseCart(cartItems));
											dispatch(
												setHardwareDrawerOpen({
													drawerType: HardwareDrawerType.Bundle,
													forceOpen: true,
												}),
											);
											setShowCartDialog(true);
										}}
									/>
								)}
							</Grid>
						</Grid>
					</>
				)}
			</Grid>
		</form>
	);
};

export { HardwareCheckout };
